import { lazy, Suspense, type JSX } from "solid-js";
import type { RouteDefinition } from "@solidjs/router";
import { Shell } from "./components/Shell";
import { Loading } from "./components/Loading";

// Lazy-loaded route modules. Per the suspense-fallback protocol,
// every Suspense wrapping these lazies MUST carry a fallback prop.
const Dashboard = lazy(() => import("./routes/Dashboard"));
const LoggedInMock = lazy(() => import("./routes/LoggedInMock"));
const MockRepositories = lazy(() => import("./routes/MockRepositories"));
const MockArtifacts = lazy(() => import("./routes/MockArtifacts"));
const MockReleases = lazy(() => import("./routes/MockReleases"));
const MockAgent = lazy(() => import("./routes/MockAgent"));
const Login = lazy(() => import("./routes/Login"));
const CliAuthorized = lazy(() => import("./routes/CliAuthorized"));
const Repositories = lazy(() => import("./routes/Repositories"));
const RepoDetail = lazy(() => import("./routes/RepoDetail"));
const RepoTree = lazy(() => import("./routes/RepoTree"));
const RepoBlob = lazy(() => import("./routes/RepoBlob"));
const RepoMirroring = lazy(() => import("./routes/RepoMirroring"));
const Releases = lazy(() => import("./routes/Releases"));
const ReleasesPortal = lazy(() => import("./routes/ReleasesPortal"));
const ModelsPortal = lazy(() => import("./routes/ModelsPortal"));
const Actions = lazy(() => import("./routes/Actions"));
const Pulls = lazy(() => import("./routes/Pulls"));
const PullDetail = lazy(() => import("./routes/PullDetail"));
const Runs = lazy(() => import("./routes/Runs"));
const RunDetail = lazy(() => import("./routes/RunDetail"));
const Workspaces = lazy(() => import("./routes/Workspaces"));
const Settings = lazy(() => import("./routes/Settings"));
const Teams = lazy(() => import("./routes/Teams"));
const TeamDetail = lazy(() => import("./routes/TeamDetail"));
const Tokens = lazy(() => import("./routes/Tokens"));
const Locks = lazy(() => import("./routes/Locks"));
const Witness = lazy(() => import("./routes/Witness"));
const ScopeClaims = lazy(() => import("./routes/ScopeClaims"));
const Convergence = lazy(() => import("./routes/Convergence"));
const MergePolicy = lazy(() => import("./routes/MergePolicy"));
const Dictionaries = lazy(() => import("./routes/Dictionaries"));
const Audit = lazy(() => import("./routes/Audit"));
const NotFound = lazy(() => import("./routes/NotFound"));
const Intelligence = lazy(() => import("./routes/Intelligence"));
const IntelSearch = lazy(() => import("./routes/IntelSearch"));
const Proposals = lazy(() => import("./routes/Proposals"));
const SettingsMcp = lazy(() => import("./routes/SettingsMcp"));
const SettingsMotor = lazy(() => import("./routes/SettingsMotor"));
const Invite = lazy(() => import("./routes/Invite"));
const InviteJoin = lazy(() => import("./routes/InviteJoin"));

// Ganforge (hosted-index hub) pages — instrument-panel shell, its own
// Suspense-with-fallback wrapper (withForge) per the suspense protocol.
import { withForge } from "./components/forge/ForgeShell";
import PublicLayout from "./components/public/PublicLayout";

const Home = lazy(() => import("./routes/Home"));
const Pricing = lazy(() => import("./routes/Pricing"));
const Faq = lazy(() => import("./routes/Faq"));
const Blog = lazy(() => import("./routes/Blog"));
const BlogPost = lazy(() => import("./routes/BlogPost"));
const Docs = lazy(() => import("./routes/Docs"));
const Benchmarks = lazy(() => import("./routes/Benchmarks"));
const Calculator = lazy(() => import("./routes/Calculator"));
const Download = lazy(() => import("./routes/Download"));
const ForgeDashboard = lazy(() => import("./routes/forge/ForgeDashboard"));
const ForgeRepositories = lazy(() => import("./routes/forge/ForgeRepositories"));
const ForgeRepoDetail = lazy(() => import("./routes/forge/ForgeRepoDetail"));
const ForgeIntelligence = lazy(() => import("./routes/forge/ForgeIntelligence"));
const ForgeProposals = lazy(() => import("./routes/forge/ForgeProposals"));
const ForgeSettings = lazy(() => import("./routes/forge/ForgeSettings"));
const ForgeProvenance = lazy(() => import("./routes/forge/ForgeProvenance"));

// OVC (Objective Verification Cloud) — monorepo frontend pages under /ovc/*
const OvcIndex = lazy(() => import("./routes/ovc/index"));
const OvcMetacognition = lazy(() => import("./routes/ovc/Metacognition"));
const OvcProjectManagement = lazy(() => import("./routes/ovc/ProjectManagement"));
const OvcVerification = lazy(() => import("./routes/ovc/Verification"));
const OvcExecutionSpeed = lazy(() => import("./routes/ovc/ExecutionSpeed"));
const OvcLeaderboard = lazy(() => import("./routes/ovc/Leaderboard"));
const OvcContextEditor = lazy(() => import("./routes/ovc/ContextEditorPage"));

function withShell(Page: () => JSX.Element) {
  return () => (
    <Shell>
      <Suspense fallback={<Loading />}>
        <Page />
      </Suspense>
    </Shell>
  );
}

function withPublic(Page: () => JSX.Element) {
  return () => (
    <PublicLayout>
      <Suspense fallback={<Loading />}>
        <Page />
      </Suspense>
    </PublicLayout>
  );
}

export const routes: RouteDefinition[] = [
  // CLI success MUST be registered before /login; same product site, CLI mode only.
  { path: "/login/cli", component: () => (
      <Suspense fallback={<Loading />}><CliAuthorized /></Suspense>
  )},
  { path: "/login", component: () => (
      <Suspense fallback={<Loading />}><Login /></Suspense>
  )},
  { path: "/", component: withPublic(Home) },
  { path: "/pricing", component: withPublic(Pricing) },
  { path: "/faq", component: withPublic(Faq) },
  { path: "/blog", component: withPublic(Blog) },
  { path: "/blog/:slug", component: withPublic(BlogPost) },
  { path: "/docs", component: withPublic(Docs) },
  { path: "/benchmarks", component: withPublic(Benchmarks) },
  { path: "/calculator", component: withPublic(Calculator) },
  { path: "/download", component: withPublic(Download) },
  { path: "/dashboard", component: withShell(Dashboard) },
  ...(import.meta.env.DEV
    ? [
        {
          path: "/mock/logged-in",
          component: () => (
            <Suspense fallback={<Loading />}>
              <LoggedInMock />
            </Suspense>
          ),
        },
        {
          path: "/mock/repositories",
          component: () => (
            <Suspense fallback={<Loading />}>
              <MockRepositories />
            </Suspense>
          ),
        },
        {
          path: "/mock/artifacts",
          component: () => (
            <Suspense fallback={<Loading />}>
              <MockArtifacts />
            </Suspense>
          ),
        },
        {
          path: "/mock/releases",
          component: () => (
            <Suspense fallback={<Loading />}>
              <MockReleases />
            </Suspense>
          ),
        },
        {
          path: "/mock/agent",
          component: () => (
            <Suspense fallback={<Loading />}>
              <MockAgent />
            </Suspense>
          ),
        },
      ]
    : []),
  // OVC (Objective Verification Cloud) — gated facets under /ovc/* (app shell).
  // Canonical product SPA home for the monorepo OVC dashboard (was crates/ganvil-cloud/frontend).
  { path: "/ovc", component: withShell(OvcIndex) },
  { path: "/ovc/metacognition", component: withShell(OvcMetacognition) },
  { path: "/ovc/project-management", component: withShell(OvcProjectManagement) },
  { path: "/ovc/verification", component: withShell(OvcVerification) },
  { path: "/ovc/execution-speed", component: withShell(OvcExecutionSpeed) },
  { path: "/ovc/leaderboard", component: withShell(OvcLeaderboard) },
  { path: "/ovc/context-editor", component: withShell(OvcContextEditor) },
  { path: "/releases", component: withPublic(ReleasesPortal) },
  { path: "/models", component: withPublic(ModelsPortal) },
  { path: "/repos", component: withShell(Repositories) },
  { path: "/repos/:owner/:repo", component: withShell(RepoDetail) },
  { path: "/repos/:owner/:repo/releases", component: withShell(Releases) },
  { path: "/repos/:owner/:repo/tree/:ref/*path", component: withShell(RepoTree) },
  { path: "/repos/:owner/:repo/tree/:ref", component: withShell(RepoTree) },
  { path: "/repos/:owner/:repo/blob/:ref/*path", component: withShell(RepoBlob) },
  { path: "/repos/:owner/:repo/pulls", component: withShell(Pulls) },
  { path: "/repos/:owner/:repo/pulls/:number", component: withShell(PullDetail) },
  { path: "/repos/:owner/:repo/actions", component: withShell(Actions) },
  { path: "/repos/:owner/:repo/actions/runs/:id", component: withShell(RunDetail) },
  { path: "/repos/:owner/:repo/runs", component: withShell(Runs) },
  { path: "/repos/:owner/:repo/runs/:id", component: withShell(RunDetail) },
  { path: "/repos/:owner/:repo/settings/source", component: withShell(RepoMirroring) },
  { path: "/repos/:owner/:repo/settings/mirroring", component: withShell(RepoMirroring) },
  { path: "/repos/:owner/:repo/locks", component: withShell(Locks) },
  { path: "/repos/:owner/:repo/witness", component: withShell(Witness) },
  { path: "/repos/:owner/:repo/scope-claims", component: withShell(ScopeClaims) },
  { path: "/repos/:owner/:repo/convergence", component: withShell(Convergence) },
  { path: "/repos/:owner/:repo/settings/merge-policy", component: withShell(MergePolicy) },
  { path: "/actions", component: withShell(Actions) },
  { path: "/pulls", component: withShell(Pulls) },
  { path: "/workspaces", component: withShell(Workspaces) },
  // Specific settings routes MUST precede the `/settings/*` catch-all.
  { path: "/settings/source", component: withShell(RepoMirroring) },
  { path: "/settings/tokens", component: withShell(Tokens) },
  { path: "/settings/mcp", component: withShell(SettingsMcp) },
  { path: "/settings/pipeline", component: withShell(SettingsMotor) },
  { path: "/settings/orgs/:org/teams", component: withShell(Teams) },
  { path: "/settings/orgs/:org/teams/invite", component: withShell(Invite) },
  { path: "/invite/join", component: () => <Suspense fallback={<Loading />}><InviteJoin /></Suspense> },
  { path: "/settings/orgs/:org/teams/:slug", component: withShell(TeamDetail) },
  { path: "/settings/orgs/:org/dictionaries", component: withShell(Dictionaries) },
  { path: "/settings/orgs/:org/audit", component: withShell(Audit) },
  { path: "/settings/*", component: withShell(Settings) },
  // Ganforge hub (appended — keep additive; see components/forge/ForgeShell).
  { path: "/forge", component: withForge(ForgeDashboard) },
  { path: "/forge/repos", component: withForge(ForgeRepositories) },
  { path: "/forge/repos/:tenant/:repo", component: withForge(ForgeRepoDetail) },
  { path: "/forge/repos/:tenant/:repo/provenance", component: withForge(ForgeProvenance) },
  { path: "/forge/intelligence", component: withForge(ForgeIntelligence) },
  { path: "/forge/proposals", component: withForge(ForgeProposals) },
  { path: "/forge/settings", component: withForge(ForgeSettings) },
  { path: "/forge/provenance", component: withForge(ForgeProvenance) },
  // Intelligence surfaces (appended here to keep the diff additive; Solid
  // Router matches by specificity, not order).
  { path: "/repos/:owner/:repo/intelligence", component: withShell(Intelligence) },
  { path: "/repos/:owner/:repo/search", component: withShell(IntelSearch) },
  { path: "/repos/:owner/:repo/proposals", component: withShell(Proposals) },
  { path: "*404", component: withShell(NotFound) },
];
